import { isApiError, requestPuppy } from '@wix/da-http-client';
import {
  all,
  call,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import {
  PapiDeviation,
  type PapiDreamsofartGenerate,
  PapiRequestDreamsofartGenerate,
} from '@wix/da-papi-types';
import ddt from '@wix/da-ddt';
import i18next from 'i18next';
import { Url } from '@wix/da-url';
import { BiData, DreamupClickCreateBiEvent } from '@wix/da-bi/pkg/events';
import { getCompliantLocalStorageItem } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/selectors';
import {
  removeCompliantLocalStorageItem,
  setCompliantLocalStorageItem,
} from '@wix/da-react-context/pkg/compliantLocalStorage/redux/actions';
import { CompliantLocalStorageKeys } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/constants';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import { updatePointsCount } from '@wix/da-shared-react/pkg/publicSession/actions';
import { validateDreamupGenerateRequest } from './validation';
import {
  generationFromPromptInitiationFailed,
  generationFromPromptInitiationSucceeded,
  generateFromPrompt,
  changePrompt,
  changeArtStyle,
  storeFormValuesForRedirectPersistence,
  loadPersistentFormValues,
  reduxInitialized,
} from './actions';
import {
  type FormStoreSlice,
  type IngressFormPersistentValues,
  SubmissionError,
} from './types';
import {
  getArtStyle,
  getFormValues,
  getPrompt,
  getShouldPullFormValuesFromLocalStorage,
  getVariationTarget,
} from './selectors';

function* handleStoreFormValuesForRedirectPersistence() {
  const artStyle = yield select(getArtStyle);
  const prompt = yield select(getPrompt);

  const storageValue: IngressFormPersistentValues = {
    artStyle,
    prompt,
  };

  yield put(
    setCompliantLocalStorageItem({
      key: CompliantLocalStorageKeys.DREAMUP_ANON_FORM,
      value: JSON.stringify(storageValue),
    })
  );

  if (typeof window !== 'undefined') {
    window.location.href = Url.joinLink(
      undefined,
      Url.dreamupLink({ persistFromStorage: true }),
      undefined,
      { daDreamup: true }
    );
  }
}

function* handleLoadPersistentFormValues() {
  const shouldPullFormValuesFromLocalStorage = yield select(
    getShouldPullFormValuesFromLocalStorage
  );
  if (!shouldPullFormValuesFromLocalStorage || typeof window === 'undefined') {
    return;
  }

  const rawPersistedLocalStorageItem = yield select(
    getCompliantLocalStorageItem,
    CompliantLocalStorageKeys.DREAMUP_ANON_FORM
  );

  try {
    const parsedFormData: IngressFormPersistentValues = JSON.parse(
      rawPersistedLocalStorageItem
    );

    yield put(changePrompt(parsedFormData.prompt));
    yield put(changeArtStyle(parsedFormData.artStyle));

    // We wipe the related local storage data and drop the query param that triggered this storage persistence to avoid
    // any unexpected behaviour on future reloads. We don't want users bookmarking dreamup with a url that would throw an
    // error every time they visit.
    yield put(
      removeCompliantLocalStorageItem(
        CompliantLocalStorageKeys.DREAMUP_ANON_FORM
      )
    );
    window.history.replaceState({}, '', Url.dreamupLink());

    ddt.info(
      'dreamup',
      'Persisted local storage to dreamup form successfully. Wiped related local storage.'
    );
  } catch (e) {
    ddt.error(
      'dreamup',
      'Failed to persist local storage value to dreamup form when requested.\n\n',
      'value we got from local storage:\n',
      rawPersistedLocalStorageItem,
      '\n\n',
      'error we got trying to parse it:\n',
      e
    );
  }
}

function decodePromptSubmissionError(response): SubmissionError {
  switch (response.errorCode) {
    case 0:
      return SubmissionError.ValidationError;
    case 1:
      return SubmissionError.ForbiddenTermsInPrompt;
    case 2:
      return SubmissionError.NotEnoughPoints;
    case 3:
      return SubmissionError.NotEnoughStashSpace;
    default:
      return SubmissionError.Unknown;
  }
}

function* handleGenerateFromPrompt(
  action: ReturnType<typeof generateFromPrompt>
) {
  const variationTarget: PapiDeviation | undefined =
    yield select(getVariationTarget);
  const formValues: FormStoreSlice['formValues'] = yield select(getFormValues);

  const requestData: PapiRequestDreamsofartGenerate = {
    prompt: formValues.prompt,
    use_points: action.payload.usePoints,
    deviationid: variationTarget?.deviationId,

    // Similarity is a value between 0 and 99 on the frontend. 0 being less similar, 99 being very similar.
    // This is inverted on the backend. 0 is very similar and 99 is less similar. So we subtract 99 to
    // invert the frontend value before sending it to the backend.
    //
    // Please note that the AI actually uses values between 0 (inclusive) and 1 (exclusive). The backend
    // converts the 0-99 we send into that range.
    similarity: variationTarget ? 100 - formValues.similarity : undefined,
    aspect_ratio: formValues.aspectRatio,
    guidance_scale: formValues.promptStrength,
    style: formValues.artStyle,
    negative_prompt: formValues.negativePrompt,
  };

  // Validate request data before sending to prevent 400 errors
  const isValid = validateDreamupGenerateRequest(requestData);
  if (!isValid) {
    yield put(
      generationFromPromptInitiationFailed(
        SubmissionError.ValidationError,
        i18next.t('common.error.validationFailed')
      )
    );
    return;
  }

  const response: PapiDreamsofartGenerate = yield call(
    requestPuppy,
    {
      url: 'generate',
      method: 'post',
      data: requestData,
    },
    () => ({
      success: true,
      generationId: 5,
      restrictions: {
        pointsBalance: 912,
      },
    }),
    'dreamsofart'
  );

  const createBiEvent = BiData<DreamupClickCreateBiEvent>({
    evid: 912,
    prompt: formValues.prompt,
    is_valid: formValues.prompt.trim().length > 0,
    image_similarity: formValues.similarity,
    aspect_ratio: formValues.aspectRatio,
    prompt_weight: formValues.promptStrength,
    art_style: formValues.artStyle,
    negative_prompt: formValues.negativePrompt,
  });

  /*
   TODO: Manually checking for errorCode === 0 until the request code is updated to handle `errorCode === 0` correctly
  */
  if (
    !response ||
    isApiError(response) ||
    (response as any).errorCode === 0 ||
    response.generationId === undefined
  ) {
    yield put(
      generationFromPromptInitiationFailed(
        decodePromptSubmissionError(response),
        (response as any).errorDescription
      )
    );
    createBiEvent.is_valid = false;
  } else {
    yield all([
      put(updatePointsCount(response.restrictions.pointsBalance)),
      put(
        generationFromPromptInitiationSucceeded(
          response.generationId,
          response.imageCount ?? 1,
          response.restrictions
        )
      ),
    ]);
  }
  yield logBiEvent(createBiEvent);
}

export function* formSaga() {
  yield all([
    takeEvery(
      storeFormValuesForRedirectPersistence,
      handleStoreFormValuesForRedirectPersistence
    ),
    takeEvery(loadPersistentFormValues, handleLoadPersistentFormValues),
    takeLatest(generateFromPrompt, handleGenerateFromPrompt),
  ]);
}

/**
 * Storybook doesn't like redux modules, so we split the saga into two parts:
 * 1. the above saga that has all the saga functionality. We'd use this in storybook.
 * 2. The below saga that gates saga execution behind the {@link reduxInitialized} action. We'd use that one
 *    in our apps.
 */
export function* formModuleSaga() {
  yield take(reduxInitialized);
  yield formSaga();
}
